/**
 * Number, date and text formatting by panel column type (ASB-4377, design §7).
 * Every chart, KPI tile and table cell formats through here, so a count reads
 * the same wherever it appears:
 *
 *   type   | axes and KPIs (context "axis") | tables and tooltips ("cell")
 *   int    | compact, 12K                   | exact, 12,431
 *   float  | compact, 1.2K                  | 1,234.57
 *   pct    | 3.4%                           | 3.42%      (value is a 0–1 fraction)
 *   date   | Oct 8                          | Wed, Oct 8, 2026   (UTC, never shifted)
 *   bool   | Yes / No                       | Yes / No
 *   url    | —                              | middle-truncated text (the cell adds the copy button)
 *   text / enum: as stored; an enum column may carry a `labels` map (value →
 *   { label, help }, e.g. text_source "youtube" → "YouTube"; A.6), which
 *   every renderer applies through valueLabel() and valueHelp().
 *   A ratio column may carry `minBase` (A.6): smallBase() says when the
 *   denominator is under it and what the tooltip appends.
 *
 * Nothing here reads the DOM, so it is unit-tested directly.
 */
export const COLUMN_TYPES = ["int", "float", "pct", "text", "enum", "bool", "url", "date"];
export const NUMERIC_TYPES = ["int", "float", "pct"];

export const EMPTY = "—";

// Intl's default compact rounding: 12,431 reads 12K, 3,391 reads 3.4K, 3,120,000 reads 3.1M (design §7.5).
const compact = new Intl.NumberFormat("en-US", { notation: "compact" });
const exactInt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const exactFloat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const pctAxis = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });
const pctCell = new Intl.NumberFormat("en-US", {
	style: "percent",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});
const dayShort = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
	timeZone: "UTC",
});
const dayLong = new Intl.DateTimeFormat("en-US", {
	weekday: "short",
	month: "short",
	day: "numeric",
	year: "numeric",
	timeZone: "UTC",
});
const stamp = new Intl.DateTimeFormat("en-US", {
	month: "short",
	day: "numeric",
	year: "numeric",
	hour: "numeric",
	minute: "2-digit",
	timeZone: "UTC",
	timeZoneName: "short",
});

const isBlank = (value) => value === null || value === undefined || value === "";

/** "YYYY-MM-DD" (or a full ISO stamp) as a Date at UTC; null when it is not one. */
export function parseDay(value) {
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? ""));
	if (!m) return null;
	return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

/** "Oct 8" — the axis label for a day. */
export function formatDay(value) {
	const d = parseDay(value);
	return d ? dayShort.format(d) : String(value ?? EMPTY);
}

/** "Wed, Oct 8, 2026" — a day in a table or tooltip title. */
export function formatDayLong(value) {
	const d = parseDay(value);
	return d ? dayLong.format(d) : String(value ?? EMPTY);
}

/** "Oct 9, 2026, 9:15 AM UTC" — the "Updated" stamp; UTC because the DAG runs in UTC. */
export function formatTimestamp(iso) {
	if (isBlank(iso)) return null;
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return String(iso);
	return stamp.format(d);
}

/**
 * The middle of a long string replaced by an ellipsis, keeping more of the head
 * than the tail: for a URL the host and the start of the path say what it is.
 */
export function middleTruncate(value, max = 60) {
	const s = String(value ?? "");
	if (s.length <= max || max < 5) return s;
	const head = Math.ceil((max - 1) * 0.6);
	const tail = max - 1 - head;
	return `${s.slice(0, head)}…${s.slice(s.length - tail)}`;
}

const toBool = (value) => {
	if (typeof value === "string") return ["true", "1", "yes", "y"].includes(value.toLowerCase());
	return Boolean(value);
};

/**
 * One value, formatted for its column type. `context` is "cell" (tables,
 * tooltips; the default) or "axis" (axes, KPI tiles). Blank values render the
 * em dash, and anything that is not what its type says is shown as stored
 * rather than as NaN.
 */
export function formatValue(type, value, context = "cell") {
	if (isBlank(value)) return EMPTY;
	const axis = context === "axis";
	switch (type) {
		case "int": {
			const n = Number(value);
			if (!Number.isFinite(n)) return String(value);
			return axis ? compact.format(n) : exactInt.format(n);
		}
		case "float": {
			const n = Number(value);
			if (!Number.isFinite(n)) return String(value);
			return axis ? compact.format(n) : exactFloat.format(n);
		}
		case "pct": {
			const n = Number(value);
			if (!Number.isFinite(n)) return String(value);
			return axis ? pctAxis.format(n) : pctCell.format(n);
		}
		case "date":
			return axis ? formatDay(value) : formatDayLong(value);
		case "bool":
			return toBool(value) ? "Yes" : "No";
		case "url":
			return middleTruncate(value);
		default:
			return String(value);
	}
}

/**
 * The manifest's entry for one value of a column, as `{ label, help }`, or
 * null when the column maps none. A.6 writes `labels` as
 * `value → { label, help }`; a map written before 2026-10-14 (`value → text`)
 * still reads, with no help.
 */
export function labelEntry(column, value) {
	if (isBlank(value) || !column?.labels) return null;
	const entry = column.labels[String(value)];
	if (entry === undefined || entry === null) return null;
	if (typeof entry === "object") {
		return { label: entry.label ?? String(value), help: entry.help ?? null };
	}
	return { label: String(entry), help: null };
}

/**
 * The display text for one value of a column: its entry in the column's
 * `labels` map when the manifest gives one, otherwise the value formatted for
 * its type. Blank values stay blank (the em dash), never a label.
 */
export function valueLabel(column, value, context = "cell") {
	if (isBlank(value)) return EMPTY;
	return labelEntry(column, value)?.label ?? formatValue(column?.type, value, context);
}

/** The tooltip text the manifest gives a value (A.6 `labels[value].help`), or null. */
export const valueHelp = (column, value) => labelEntry(column, value)?.help ?? null;

/**
 * A column label as a unit inside a sentence ("25,225 flagged", "Based on
 * fewer than 50 checked", "97,186 NLP records in total"): the first letter
 * lower-cased, and only when the second is lower-case, so an acronym or a
 * mixed-case word keeps its capitals ("NLP records", "URLs").
 */
export function unitOf(label) {
	const s = String(label ?? "");
	if (s.length >= 2 && /[a-z]/.test(s[1])) return s[0].toLowerCase() + s.slice(1);
	return s;
}

/**
 * A column label as the unit of a count ("1 record", "25,225 records", "1
 * URL"): unitOf(), made singular when the count is exactly 1 and the label
 * ends in a plain English plural — a trailing "s" ("records", "URLs", "tag
 * fires"), or "es" after ch / sh / ss / x ("fetches", "misses"). Any other
 * label ("Flagged", "% of checked", "status") is left as it is.
 */
export function unitFor(label, n) {
	const unit = unitOf(label);
	if (Number(n) !== 1) return unit;
	if (/(?:ch|sh|ss|x)es$/i.test(unit)) return unit.slice(0, -2);
	if (/[a-z]s$/i.test(unit) && !/(?:ss|us|is)$/i.test(unit)) return unit.slice(0, -1);
	return unit;
}

/**
 * The denominator column of a ratio column, from its dataset's columns, or
 * null when the column is not a ratio.
 */
export function ratioBase(column, columns) {
	if (!Array.isArray(column?.ratio) || column.ratio.length < 2) return null;
	return (columns ?? []).find((c) => c.key === column.ratio[1]) ?? { key: column.ratio[1] };
}

/**
 * A.6 `minBase`: whether a ratio's denominator (after aggregation) is below
 * the column's floor, and the note to append to its tooltip when it is:
 * "Based on fewer than 50 checked", the unit being the denominator column's
 * label, lower-cased. `den` is the denominator's aggregated value.
 */
export function smallBase(column, columns, den) {
	const min = Number(column?.minBase);
	if (!Number.isFinite(min) || min <= 0) return null;
	if (isBlank(den)) return null;
	const n = Number(den);
	if (!Number.isFinite(n) || n >= min) return null;
	const base = ratioBase(column, columns);
	const unit = unitOf(base?.label ?? base?.key ?? "records");
	return { min, den: n, note: `Based on fewer than ${exactInt.format(min)} ${unit}` };
}

/** The formatter for a column, bound to its type: `cellFormatter(col)(value)`. */
export const cellFormatter = (column) => (value) => formatValue(column?.type, value, "cell");
export const axisFormatter = (column) => (value) => formatValue(column?.type, value, "axis");
